import { useEffect, useState } from 'react';
import { nanoid } from 'nanoid';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useSession } from './store/session';
import Header from './components/Header';
import Chat from './components/Chat';
import ApiKeysModal from './components/ApiKeysModal';
import SaverPanel from './components/SaverPanel';
import SocialFeedModal from './components/SocialFeedModal';
import StripeModal from './components/StripeModal';
import SavedDriveFilesModal from './components/SavedDriveFilesModal';
import NewChatModal from './components/NewChatModal';
import TemplatesDropdownModal from './components/TemplatesDropdownModal';
import { DEVICE } from './lib/device';
import { installContextGlobal } from './lib/context';
import './components/app.css';

export default function App() {
  const init = useSession(s => s.init);
  const addStaged = useSession(s => s.addStaged);
  const [showKeys, setShowKeys] = useState(false);
  const [saversCategory, setSaversCategory] = useState<string | null>(null);
  const [showTemplatesDropdown, setShowTemplatesDropdown] = useState(false);
  const [showSocial, setShowSocial] = useState(false);
  const [showStripe, setShowStripe] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);

  useEffect(() => { init(); installContextGlobal(); }, [init]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="app scene-perspective"
    >
      <Header 
        onOpenKeys={() => setShowKeys(true)} 
        onOpenSavers={(cat) => {
          if (cat) {
            setSaversCategory(cat);
          } else {
            setShowTemplatesDropdown(true);
          }
        }} 
        onOpenSocial={() => setShowSocial(true)}
        onOpenStripe={() => setShowStripe(true)}
        onOpenSaved={() => setShowSaved(true)}
        onOpenNewChat={() => setShowNewChat(true)}
      />
      <main className="app__main">
        <Chat />
      </main>
      <footer className="app__footer">
        <span className="sc">protolive studio</span>
        <span className="app__footer-sep">·</span>
        <Link to="/privacy" className="sc">privacy</Link>
        <span className="app__footer-sep">·</span>
        <Link to="/terms" className="sc">terms</Link>
        {DEVICE.isStandalone && <><span className="app__footer-sep">·</span><span className="sc">installed</span></>}
      </footer>
      <AnimatePresence>
        {showNewChat && <NewChatModal onClose={() => setShowNewChat(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {showKeys && <ApiKeysModal onClose={() => setShowKeys(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {saversCategory && (
          <SaverPanel
            category={saversCategory}
            onClose={() => setSaversCategory(null)}
            onInsert={(prompt, refs) => {
              for (const r of refs) addStaged({ ...r, id: nanoid() });
              useSession.setState(s => ({ ...s }));
              // pipe prompt into the composer textarea via a custom event
              window.dispatchEvent(new CustomEvent('protolive:insert-prompt', { detail: prompt }));
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showSocial && <SocialFeedModal onClose={() => setShowSocial(false)} onInsert={(prompt) => {
          window.dispatchEvent(new CustomEvent('protolive:insert-prompt', { detail: prompt }));
        }} />}
      </AnimatePresence>
      <AnimatePresence>
        {showStripe && <StripeModal onClose={() => setShowStripe(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {showSaved && <SavedDriveFilesModal onClose={() => setShowSaved(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {showTemplatesDropdown && (
          <TemplatesDropdownModal
            onClose={() => setShowTemplatesDropdown(false)}
            onSelect={(cat) => setSaversCategory(cat)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
